import * as React from 'react';
import { Location } from 'history';

import { HonestWork, PopupDirection } from '@cian/honestwork-component';

import { IComment } from '../../types/posts';
import { formatDateFull } from '../../../app/helpers/format_helper';
import { canEditDelete } from '../../../app/helpers/comments_helper';
import { IUser } from '../../types/user';
import { permissionsFull, permissionsExtend } from './permissions';
import { CommentForm } from './comment_form';
import { ICommentState } from '../../types/posts';
import { getCleanText } from '../../../app/helpers/make_html_helper';
import { avatar } from './avatar';

import s from './comments.css';

interface IProps {
  location: Location;
  user: IUser;
  likesShown: boolean;
  canComment: boolean;
  answer: IComment;
  onCommentDelete(id: number, email: string): void;
  onCommentHide(id: number, email: string): void;
  onCommentSubmit(comment: string, userId: number, fullName: string, email: string, parentID: number | null, subs: boolean): void;
  commentsState: ICommentState;
  parentID: number | null;
  onCommentEdit(id: number, comment: string, email: string): void;
  cardType: string;
}

export const Answer: React.FC<IProps> = ({
  location,
  user,
  likesShown,
  canComment,
  answer,
  onCommentDelete,
  onCommentHide,
  onCommentSubmit,
  commentsState,
  parentID,
  onCommentEdit,
  cardType,
}) => {
  const [editingComment, setEditingComment] = React.useState(false);
  const [replyTo, setReplyTo] = React.useState('');
  const [savedFields, setSavedFields] = React.useState<{
    comment: string;
    name: string;
    email: string;
    subscribe: boolean;
  } | undefined>(undefined);

  const prevLoadedRef = React.useRef(commentsState.loaded);

  React.useEffect(() => {
    const wasLoaded = prevLoadedRef.current;
    prevLoadedRef.current = commentsState.loaded;

    if (!commentsState.error && commentsState.loaded && commentsState.loaded !== wasLoaded) {
      setReplyTo('');
      setEditingComment(false);
    }
  }, [commentsState.error, commentsState.loaded]);

  const isSelected = () => {
    const hash = location.hash;
    return hash && Number(hash.replace('#', '')) === answer.id;
  };

  const isAuthor = user && answer.user && user.userId === answer.user.userId;
  const isCanEdit = isAuthor && canEditDelete(answer.datePublish) && !editingComment;
  const isCanDelete = isAuthor && canEditDelete(answer.datePublish);
  const isCanHide = user && (user.roles.includes(permissionsFull) || user.roles.includes(permissionsExtend));

  const editComment = (replyToName?: string) => {
    setReplyTo(replyToName || '');
    setEditingComment(true);
  };

  const saveFields = (comment: string, name: string, email: string, subscribe: boolean) => {
    setSavedFields({ comment, name, email, subscribe });
  };

  const getUserName = () => {
    const name = answer.user && answer.user.userId > 0
      ? (answer.user.fullName || answer.user.companyName || `ID: ${answer.user.userId}`)
      : 'Аноним';

    if (answer.user?.specialistLink) {
      return <a className={s['comments__item_name-text']} href={`${answer.user.specialistLink}`}>{name}</a>;
    }

    return <span className={s['comments__item_name-text']}>{name}</span>;
  };

  return (
    <div
      id={`${answer.id}`}
      className={`
        ${s['comments__list-answer_item']}
        ${isSelected() ? s['comments__list_item-new'] : ''}`}
    >
      <div className={s['comments__item_wrap-info']}>
        {answer.user && avatar(answer.user)}
        {!answer.user && <div className={s['comments__form_photo-default']} />}
        <div className={s['comments__item_info']}>
          <span className={s['comments__item_name']}>
            {getUserName()}
            {answer.user && answer.user.userTrust &&
              <HonestWork
                className={s['comments__item_honest']}
                direction={PopupDirection.RIGHT}
              />
            }
          </span>
          <span className={s['comments__item_date']}>
            {formatDateFull(answer.datePublish)}
          </span>
        </div>
      </div>
      <p
        dangerouslySetInnerHTML={{ __html: getCleanText(answer.comment) }}
        className={s['comments__item_text']}
      />
      <div className={`${s['comments__item_bottom-line']} ${s['answer__item_bottom-line']}`}>
        {isCanEdit &&
          <span onClick={() => editComment(answer.user.fullName || answer.user.companyName)}>
            Редактировать
          </span>
        }
        {isCanDelete &&
          <span onClick={() => onCommentDelete(answer.id, answer.user.email)}>
            Удалить
          </span>
        }
        {isCanHide &&
          <span onClick={() => onCommentHide(answer.id, answer.user.email)}>
            Скрыть
          </span>
        }
      </div>
      {editingComment &&
        <CommentForm
          onOpen={() => {}}
          onClose={() => {
            setReplyTo('');
            setEditingComment(false);
          }}
          onCommentSubmit={(
            comment: string,
            userId: number,
            fullName: string,
            email: string,
            pid: number | null,
            subs: boolean,
          ) => onCommentSubmit(comment, userId, fullName, email, pid, subs)}
          commentsState={commentsState}
          replyTo={replyTo}
          parentID={parentID}
          editingComment={editingComment}
          onCommentEdit={onCommentEdit}
          isOpened={true}
          user={user}
          comment={answer}
          saveFields={saveFields}
          savedFields={savedFields}
          containerStyle={s['answer__comment-form']}
          cardType={cardType}
        />
      }
    </div>
  );
};
